import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { Menu, type MenuAction } from "../../components/Menu";
import { useAuth } from "../../auth/AuthProvider";
import { useToast } from "../../components/Toast";
import { Icon } from "../../components/Icon";
import { Modal } from "../../components/ui";

/**
 * الإجراءات السريعة على عرض السعر — قائمةٌ لا دخولٌ إلى العرض.
 *
 * ما كان يحتاج فتحَ العرض ثم الضغط صار نقرتين من القائمة: معاينة، تعديل،
 * إرسال (للمسوّدة)، قبول ورفض (للمُرسَل)، وحذف للمدير. والإجراء الذي لا
 * يصلح للحالة يُعرض معطّلًا بسببه لا يُخفى — الغياب بلا تفسير يُقرأ عطلًا.
 */
export function QuotationActions({
  quotation,
  onPreview,
  onChanged,
}: {
  quotation: { id: string; number: string; status: string };
  onPreview: () => void;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  /* قبولُ عرضِ محتملٍ يعيد إشارةً: صار مؤهَّلًا — فتُفتح دعوةُ التحويل هنا. */
  const [qualify, setQualify] = useState<string | null>(null);

  const done = (msg: string) => {
    toast(msg);
    void queryClient.invalidateQueries({ queryKey: ["quotations"] });
    onChanged();
  };

  const send = useMutation({
    mutationFn: () =>
      api(`/sales/quotations/${quotation.id}/send`, { method: "POST" }),
    onSuccess: () => done("أُرسل العرض"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الإرسال"),
  });

  const decide = useMutation({
    mutationFn: (decision: "ACCEPTED" | "REJECTED") =>
      api(`/sales/quotations/${quotation.id}/decide`, {
        method: "POST",
        body:
          decision === "REJECTED"
            ? { decision, rejectReason: "رُفض من القائمة السريعة" }
            : { decision },
      }),
    onSuccess: (r, decision) => {
      const res = r as
        { needsConversion?: boolean; leadId?: string | null } | undefined;
      /* الموافقةُ لم تُسجَّل: العميلُ محتملٌ بعد — التحويلُ أولًا. */
      if (decision === "ACCEPTED" && res?.needsConversion && res.leadId) {
        toast("حوّل العميل المحتمل إلى عميل لتُسجَّل الموافقة");
        setQualify(res.leadId);
        return;
      }
      done(decision === "ACCEPTED" ? "قُبل العرض" : "رُفض العرض");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحفظ"),
  });

  const remove = useMutation({
    mutationFn: () =>
      api(`/sales/quotations/${quotation.id}`, { method: "DELETE" }),
    onSuccess: () => done("حُذف العرض"),
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحذف"),
  });

  /* العقد يُبنى من العرض فتُنسخ بياناتُه وبنودُه — ثم يُفتح ليُراجَع. */
  const toContract = useMutation({
    mutationFn: () =>
      api<{ id: string }>("/sales/contracts/from-quotation", {
        method: "POST",
        body: {
          quotationId: quotation.id,
          includeLines: true,
          includeDefaults: true,
        },
      }),
    onSuccess: (created) => {
      toast("أُنشئ العقد من العرض");
      void queryClient.invalidateQueries({ queryKey: ["contracts"] });
      navigate(`/sales/contracts/${created.id}`);
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر إنشاء العقد"),
  });

  const isDraft = quotation.status === "DRAFT";
  const isSent = quotation.status === "SENT";

  const actions: MenuAction[] = [
    { key: "preview", label: "معاينة", icon: "search", onSelect: onPreview },
    {
      key: "open",
      label: "فتح وتعديل",
      icon: "edit",
      onSelect: () => navigate(`/sales/quotations/${quotation.id}`),
    },
    {
      key: "send",
      label: "إرسال",
      icon: "send",
      onSelect: () => send.mutate(),
      disabled: !isDraft,
      title: isDraft ? undefined : "يُرسَل من المسوّدة فقط",
      separated: true,
    },
    {
      key: "accept",
      label: "وضعه مقبولًا",
      icon: "check",
      onSelect: () => decide.mutate("ACCEPTED"),
      disabled: !isSent,
      title: isSent ? undefined : "يُقرَّر بعد الإرسال",
    },
    {
      key: "reject",
      label: "وضعه مرفوضًا",
      icon: "x",
      onSelect: () => decide.mutate("REJECTED"),
      disabled: !isSent,
      title: isSent ? undefined : "يُقرَّر بعد الإرسال",
    },
    {
      key: "contract",
      label: "إنشاء عقد من العرض",
      icon: "clipboard",
      onSelect: () => toContract.mutate(),
      /* المسودّة لم يوافق عليها أحد، فلا يُبنى عليها التزام. */
      disabled: quotation.status === "DRAFT",
      title:
        quotation.status === "DRAFT"
          ? "أرسل العرض أولًا — العقد التزامٌ بما وافق عليه العميل"
          : undefined,
      separated: true,
    },
  ];

  if (atLeast("MANAGER")) {
    actions.push({
      key: "delete",
      label: "حذف",
      icon: "trash",
      onSelect: () => {
        if (window.confirm(`حذف العرض ${quotation.number}؟`)) remove.mutate();
      },
      danger: true,
      separated: true,
    });
  }

  return (
    <>
      <Menu actions={actions} label={`إجراءات العرض ${quotation.number}`} />
      {qualify && (
        <QualifyPrompt
          leadId={qualify}
          quotationId={quotation.id}
          number={quotation.number}
          onClose={() => setQualify(null)}
        />
      )}
    </>
  );
}

/**
 * «صار مؤهَّلًا» — تُفتح لحظةَ قبول أوّل عرضٍ لمحتمل.
 *
 * العرضُ قُبل والعميلُ ما زال محتملًا: العقدُ والفاتورةُ لا تُبنيان عليه
 * حتى يُحوَّل. فتُقال الإشارة في مكانها، وزرٌّ واحد يقود إلى التحويل
 * مفتوحًا على نافذته — لا رسالةٌ تُقرأ ثم تُنسى.
 */
export function QualifyPrompt({
  leadId,
  quotationId,
  number,
  onClose,
}: {
  leadId: string;
  quotationId: string;
  number: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  return (
    <Modal
      title="العميل أصبح مؤهَّلًا"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            لاحقًا
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              onClose();
              navigate(`/crm/leads/${leadId}?convert=1&accept=${quotationId}`);
            }}
          >
            <Icon name="star" size={14} />
            حوّله إلى عميل الآن
          </button>
        </>
      }
    >
      <div className="qm-hero">
        <span className="qm-star">
          <Icon name="star" size={18} />
        </span>
        <div>
          وافق العميل على عرض السعر <strong className="num">{number}</strong> —
          وهو ما زال عميلًا محتملًا. لتُسجَّل الموافقةُ يلزم تحويله إلى عميل
          (فردًا أو شركة) أولًا؛ وبعد التحويل تُسجَّل الموافقةُ تلقائيًّا على
          حسابه وتُبنى عليها العقودُ والفواتير.
        </div>
      </div>
    </Modal>
  );
}
